// ATE-CR-117
import { useEffect, useState } from "react";

import { listCreditTransactions, type CreditTransaction } from "@/api/credits.api";

interface Props {
  readonly tenantId: string;
}

// ATE-CR-117
export function TransactionsTab({ tenantId }: Props): JSX.Element {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    listCreditTransactions(tenantId, ctrl.signal)
      .then((data) => {
        if (cancelled) return;
        setTransactions(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [tenantId, retryKey]);

  if (loading) {
    return <div role="status" className="credits-tab__loading">Loading transactions…</div>;
  }

  if (error) {
    return (
      <div role="alert" className="credits-tab__error">
        Failed to load transactions: {error.message}
        <button type="button" onClick={() => setRetryKey((k) => k + 1)}>Retry</button>
      </div>
    );
  }

  return (
    <table className="credits-table">
      {/* ATE-CR-117 — task 11 FIX: backend uses `created_by` not `actor`, and
          `note` may be null. */}
      <thead>
        <tr>
          <th scope="col">Created</th>
          <th scope="col">Type</th>
          <th scope="col">Resource</th>
          <th scope="col">Amount</th>
          <th scope="col">Created By</th>
          <th scope="col">Note</th>
        </tr>
      </thead>
      <tbody>
        {transactions.map((tx) => (
          <tr key={tx.id}>
            <td>{new Date(tx.created_at).toLocaleString()}</td>
            <td>{tx.type}</td>
            <td>{tx.resource}</td>
            <td>{tx.amount}</td>
            <td>{tx.created_by ?? "—"}</td>
            <td>{tx.note ?? "—"}</td>
          </tr>
        ))}
        {transactions.length === 0 && (
          <tr>
            <td colSpan={6} className="credits-table__empty">
              No transactions found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
